import type { ComponentProps } from 'react';
import { Text } from './Text';
import { formatMoney, formatTotalsByCurrency, type MoneyLike } from '../utils/money';

export type MoneyTone = 'default' | 'muted' | 'success' | 'error';

const TONE_COLOR: Record<MoneyTone, string> = {
  default: '$textOnSurface',
  muted: '$textSubdued',
  success: '$textSuccess',
  error: '$textError',
};

export type MoneySize = 'sm' | 'md' | 'lg';

const SIZE_VARIANT: Record<MoneySize, ComponentProps<typeof Text>['variant']> = {
  sm: 'caption',
  md: 'body',
  lg: 'title',
};

export interface MoneyTextProps
  extends Omit<ComponentProps<typeof Text>, 'children'> {
  /** A single `Money` value or a list of per-currency totals (the
   *  cross-currency rollup shape per ADR-0048). Pass one or the
   *  other — not both. */
  money?: MoneyLike;
  totals?: MoneyLike[];
  /** Optional Intl locale; defaults to `'en-US'`. */
  locale?: string;
  /** Visual tone. Default `default`. `color` overrides this when set. */
  tone?: MoneyTone;
  /** Text size — maps to a Text variant. Default `md`. */
  size?: MoneySize;
  /** Rendered when both `money` and `totals` are absent / empty. */
  fallback?: string;
}

/**
 * Canonical money display. Centralises `Intl.NumberFormat`-based
 * formatting + the tone tokens (`$textSuccess` / `$textError` etc.) so
 * scattered `<Text>{`${value} ${currency}`}</Text>` patterns collapse
 * into one component. Per ADR-0076 / Phase 4.
 *
 * Pass `money={{ amount, currency }}` for a single value, or
 * `totals={[…]}` for cross-currency rollups — the helper renders
 * "$1,200 + €450" when there's more than one entry.
 */
export function MoneyText({
  money,
  totals,
  locale,
  tone = 'default',
  size = 'md',
  fallback = '',
  color,
  ...rest
}: MoneyTextProps) {
  let formatted = '';
  if (money) {
    formatted = formatMoney(money, locale);
  } else if (totals?.length) {
    formatted = formatTotalsByCurrency(totals, locale);
  }
  if (!formatted) formatted = fallback;

  return (
    <Text
      variant={SIZE_VARIANT[size]}
      color={color ?? TONE_COLOR[tone]}
      {...rest}
    >
      {formatted}
    </Text>
  );
}
